import { useEffect, useState } from "react";
import { toast } from "sonner";
import { teacherApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Database, Download, Trash2, AlertTriangle, Shield, Wrench, Gift, Sparkles, FileSearch, ArrowLeft, History, Mail, RefreshCw, TrendingUp, Eye, MessageSquareWarning, Check, Archive, MessageCircleReply, Send, X, Plus } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import { Link } from "react-router-dom";
import { Section, KpiTile, SCOPE_LABELS } from "./_shared";

// =========================== REJUDGE ===========================
function RejudgeTab() {
  const [scope, setScope] = useState("all");
  const [limit, setLimit] = useState(100);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState(null);
  const [sample, setSample] = useState([]);

  const loadPending = async () => {
    // Use the backend `quality=pending` filter for an accurate pre-judge
    // count across ALL sources (not just ai_phase3). Also fetch the warning
    // count in a separate call so numbers align with the /questions view.
    const [pendingRes, warnedRes] = await Promise.all([
      teacherApi.get("/questions?source=shared&quality=pending"),
      teacherApi.get("/questions?source=shared&quality=warning"),
    ]);
    setPending({ pre_judge: pendingRes.data.length, warned: warnedRes.data.length });
    // 5 oldest pending items for the "Kolejka do przeglądu" preview.
    // Backend returns unsorted; sort client-side by created_at asc.
    const sorted = [...pendingRes.data].sort((a, b) =>
      (a.created_at || "").localeCompare(b.created_at || "")
    );
    setSample(sorted.slice(0, 5));
  };
  useEffect(() => { loadPending(); }, []);

  const submit = async () => {
    setBusy(true); setStats(null);
    try {
      const { data } = await teacherApi.post("/admin/rejudge-questions", {
        scope: scope === "all" ? null : scope,
        limit: Number(limit),
      });
      setStats(data);
      toast.success(`Przejrzano: ${data.reviewed}, ostrzeżeń: ${data.warned}`);
      loadPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd re-judge");
    } finally { setBusy(false); }
  };

  return (
    <Section icon={Sparkles} title="Re-judge istniejących pytań">
      <div className="space-y-4 max-w-3xl">
        {pending && (
          <div className="brut border-2 border-[#0F172A] rounded-md p-3 bg-[#FEF3C7] text-sm font-bold" data-testid="rejudge-pending">
            Pre-judge (do oceny): <span className="text-2xl">{pending.pre_judge}</span> ·
            Z ostrzeżeniem: <span className="text-2xl ml-2">{pending.warned}</span>
          </div>
        )}

        {/* ---------- Kolejka do przeglądu (5 najstarszych pending) ---------- */}
        {sample.length > 0 && (
          <div className="brut border-2 border-[#0F172A] rounded-md bg-white" data-testid="review-queue-widget">
            <div className="border-b-2 border-[#0F172A] px-4 py-2 flex items-center gap-2 bg-[#F1F5F9]">
              <FileSearch className="w-4 h-4" strokeWidth={2.5} />
              <span className="font-black uppercase tracking-wider text-sm">
                Kolejka do przeglądu — 5 najstarszych
              </span>
              <span className="ml-auto text-xs text-[#64748B]">
                z {pending?.pre_judge ?? 0} oczekujących
              </span>
            </div>
            <ul className="divide-y divide-[#E2E8F0]" data-testid="review-queue-list">
              {sample.map((q, idx) => (
                <li key={q.id} className="px-4 py-2 text-sm flex items-start gap-3">
                  <span className="font-black text-[#94A3B8] text-xs mt-0.5 w-4 shrink-0">
                    {idx + 1}.
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold truncate" title={q.text}>{q.text}</div>
                    <div className="text-xs text-[#64748B] mt-0.5">
                      <b>{q.topic}</b> · {q.scope} · {q.source || "manual"}
                      {q.created_at && (
                        <span title={q.created_at}>
                          {" · "}dodane {new Date(q.created_at).toLocaleDateString("pl")}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="border-t-2 border-[#0F172A] px-4 py-3 flex gap-2 flex-wrap bg-[#F8FAFC]">
              <Link to="/teacher/questions?quality=pending" data-testid="review-queue-manual-link">
                <Button
                  size="sm"
                  className="brut brut-hover bg-white border-2 border-[#0F172A] font-bold rounded-md text-xs"
                >
                  <Eye className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} />
                  Przejrzyj ręcznie ({pending?.pre_judge ?? 0})
                </Button>
              </Link>
              <p className="text-xs text-[#64748B] flex-1 min-w-[240px]">
                Pełny przegląd otwiera widok „Pytania&rdquo; z filtrem <b>🕒 Do oceny</b>.
                Uruchamianie Judge AI poniżej dotyczy TYLKO wygenerowanych pytań AI —
                nie legacy/ręcznych.
              </p>
            </div>
          </div>
        )}
        {/* -------------------------------------------------------------------- */}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="font-bold">Zakres</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="mt-2 brut border-2 border-[#0F172A] rounded-md h-11 font-bold" data-testid="rejudge-scope-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="brut bg-white border-2 border-[#0F172A] rounded-md">
                <SelectItem value="all">Wszystkie zakresy</SelectItem>
                {Object.entries(SCOPE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-bold">Limit (max pytań w tym wywołaniu)</Label>
            <Input
              type="number" min={20} max={1000} step={20} value={limit}
              onChange={(e) => setLimit(e.target.value)}
              className="mt-2 brut-input border-2 border-[#0F172A] rounded-md h-11"
              data-testid="rejudge-limit-input"
            />
          </div>
        </div>
        <Button
          onClick={submit}
          disabled={busy}
          className="brut brut-hover bg-[#E0E7FF] text-[#0F172A] font-bold rounded-md"
          data-testid="rejudge-submit-btn"
        >
          <Sparkles className="w-4 h-4 mr-2" strokeWidth={2.5} />
          {busy ? "Oceniam..." : "Uruchom re-judge"}
        </Button>
        {stats && (
          <div className="brut border-2 border-[#0F172A] rounded-md p-3 bg-white text-sm" data-testid="rejudge-stats">
            ✅ Przejrzano: {stats.reviewed} · OK: {stats.ok} · ⚠️ Z warningiem: {stats.warned}
          </div>
        )}
        <p className="text-xs text-[#64748B]">
          Pytania flagowane jako wątpliwe NIE są usuwane — pojawiają się w filtrze „Wątpliwe&rdquo; w widoku Pytania.
        </p>
      </div>
    </Section>
  );
}


export default RejudgeTab;
